import type { AppErrorBody } from '@edal-tracker/shared';
import { apiEvents } from './apiEvents.js';
import { isStop, stopSignal } from './stopSignal.js';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly userMessage: string,
    public readonly requestId?: string,
    // Optional structured recovery context — populated for error codes
    // like OTHER_SESSION_OPEN that need to hand the client recovery info.
    public readonly details?: unknown,
  ) {
    super(`${code}: ${userMessage}`);
    this.name = 'ApiError';
  }
}

export type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiOptions = {
  method?: ApiMethod;
  body?: unknown;
  signal?: AbortSignal;
  /** Extra request headers — e.g. the saw kiosk's `X-Badge` (kept out of the URL). */
  headers?: Record<string, string>;
  /**
   * מה לומר כשהשרת **לא** החזיר הודעה משלו.
   *
   * 27/09/2026, בסריקת ה-`fetch` הגולמיים: לכל קריאה ידנית היה משפט נפילה
   * משלה („שגיאה בהעלאת תמונה", „שגיאה בייבוא הספקים"), ו„שגיאת רשת" הכללי
   * היה מוחק אותם. המשפט של הקורא נשמר — הוא זה שיודע מה נכשל.
   */
  fallbackMessage?: string;
};

/**
 * ההודעה כשאין הודעה מהשרת — 502 מ-nginx בזמן פריסה, 504 אחרי 120 שניות,
 * 413 מעל המכסה. אלה בדיוק המקרים שהוצגו „שגיאת רשת", ואז האדם ניסה שוב
 * ושוב בלי לדעת שאין מה לתקן בצד שלו.
 *
 * מחזירה `null` כשלסטטוס אין משפט מיוחד, כדי שהמשפט של הקורא יזכה.
 */
function statusMessage(status: number): string | null {
  if (status === 413) return 'הקובץ גדול מדי לשליחה. שמור אותו מחדש בדחיסה או פצל אותו, ונסה שוב.';
  if (status === 502 || status === 503 || status === 504) {
    return `השרת לא ענה עד הסוף (${status}). זו תקלה בשרת ולא בנתונים — המתן רגע ונסה שוב.`;
  }
  return null;
}

/**
 * הטקסט שמוצג לאדם, מכל שגיאה.
 *
 * ⚠ `ApiError.message` הוא „CODE: טקסט" (ראה ה-`super` למעלה), ולכן מסך
 * שקורא `e.message` ישירות מציג לאדם `VALIDATION: חסר מספר פרויקט` — קוד
 * לטיני בתוך משפט עברי, שגם נשבר ב-bidi. המסכים שידעו זאת כתבו
 * `(e as {userMessage?}).userMessage ?? (e as Error).message` בעצמם — **24
 * העתקים** של אותה שורה, נמדד 27/09/2026. זו ההגדרה האחת.
 *
 * duck-typing ולא `instanceof`: שני עתקים של המודול (build אחד ו-test אחר)
 * מייצרים שני מחלקות `ApiError` שונות, ואז `instanceof` שקר על שגיאה תקינה.
 */
export function errorText(e: unknown, fallback = 'הפעולה נכשלה. נסה שוב.'): string {
  if (e !== null && typeof e === 'object') {
    const u = (e as { userMessage?: unknown }).userMessage;
    if (typeof u === 'string' && u !== '') return u;
  }
  if (e instanceof Error && e.message !== '') return e.message;
  return fallback;
}

/**
 * Every failure leaves here as an ApiError. That is the whole point.
 *
 * Two could escape as raw errors, and both produced the same useless sentence
 * on screen — every caller in the app writes
 * `e instanceof ApiError ? e.userMessage : 'שגיאה לא צפויה'`, so anything
 * that is not an ApiError tells the person nothing at all:
 *
 *   1. **fetch() itself rejecting.** No network, the server down, DNS gone, a
 *      request cut off mid-flight. The browser throws a bare TypeError.
 *   2. **A 2xx body that is not JSON.** The classic cause is a browser tab left
 *      open across a deploy: it asks for a path the new build no longer serves
 *      and nginx hands back the SPA's index.html with a 200. `res.json()` then
 *      throws a SyntaxError from inside a successful request.
 *
 * Eli hit the second one on 2026-08-27: "ניסיתי להכנס למערכת וקיבלתי
 * במסך Login שגיאה לא צפויה" — and neither server had a record of the
 * request, because it never arrived in a form either of them recognised.
 *
 * Both now carry a sentence that says what to DO. A person who is told to press
 * Ctrl+F5 presses it; a person told "שגיאה לא צפויה" telephones somebody.
 */
/**
 * מזהה העמדה — קבוע לדפדפן (מנכ"ל 03/11/2026). כל עמדות המפעל יוצאות מ-IP אחד,
 * והשרת מגביל קצב לפי העמדה ולא לפי ה-IP, ורושם אותו על כל סירוב.
 */
let memKioskId: string | null = null;
export function kioskId(): string {
  if (memKioskId) return memKioskId;
  const fresh = (): string => {
    try { return crypto.randomUUID().replace(/-/g, ''); } catch { return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`; }
  };
  try {
    const stored = localStorage.getItem('edal.kioskId');
    if (stored && /^[A-Za-z0-9_-]{8,64}$/.test(stored)) return (memKioskId = stored);
    const id = fresh();
    localStorage.setItem('edal.kioskId', id);
    return (memKioskId = id);
  } catch {
    return (memKioskId = fresh());
  }
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(path.startsWith('/api/scan') ? { 'X-Kiosk-Id': kioskId() } : {}),
        ...opts.headers,
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    // A cancelled request is not a failure to report — the screen that
    // cancelled it has already moved on.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    apiEvents.emit('unreachable');
    throw new ApiError(
      0, 'NETWORK',
      'לא ניתן להגיע לשרת. יש לבדוק את חיבור הרשת ולנסות שוב.',
    );
  }

  if (res.status === 401) {
    apiEvents.emit('unauthenticated');
    // continue to throw — caller's catch path still runs
  }
  // nginx עונה 502/504 כשהשרת מופעל מחדש בעדכון — לא תקלה של המסך (19/10/2026).
  // ⚠ לא 503: השרת עצמו עונה 503 (למשל health שלא תקין) — הוא כאן, רק לא מרוצה.
  if (res.status === 502 || res.status === 504) apiEvents.emit('unreachable');

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Partial<AppErrorBody>;
    const err = new ApiError(
      res.status,
      body.code ?? (res.status === 413 ? 'FILE_TOO_LARGE' : 'NETWORK'),
      body.message ?? statusMessage(res.status) ?? opts.fallbackMessage ?? 'שגיאת רשת',
      body.requestId,
      body.details,
    );
    // עצירה אמיתית (אי-התאמה חוסמת, עצירת ייצור, ECO, FAI, בדיקת הנדסה) → מסך עצירה גדול
    // בעמדה (אלי 21/10/2026). המסך שקרא ממשיך לטפל בשגיאה כרגיל.
    if (isStop(err)) stopSignal.emit(err);
    throw err;
  }

  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    // A 200 that is not JSON is almost always this build's HTML being served
    // for a path this tab still believes in.
    throw new ApiError(
      res.status, 'STALE_CLIENT',
      'הדף שפתוח מריץ גרסה ישנה של המערכת. יש לרענן את הדף עם Ctrl+F5 ולנסות שוב.',
    );
  }
}

/**
 * העלאת קובץ — ויוצאת מכאן כ-`ApiError`, בדיוק כמו `api()`.
 *
 * העלאות לא יכולות לעבור דרך `api()` כי הן שולחות `FormData` ולא JSON, ולכן
 * כל אחת מהן נכתבה כ-`fetch` גולמי שזורק `new Error(...)`. והמחיר התגלה
 * ב-27/09/2026: אלי ניסה להעלות טבלת פרופילים לחיתוך וקיבל „החיבור לשרת
 * נכשל — נסה שוב." הרשת היתה תקינה לגמרי. המסך כותב
 * `e instanceof ApiError ? e.userMessage : 'החיבור לשרת נכשל'`, והשגיאה
 * שהגיעה לא היתה `ApiError` — אז ההודעה האמיתית של השרת, זו שאומרת *מה*
 * לא בסדר בקובץ, נזרקה לפח והוחלפה במשפט ששולח אותו לבדוק את הרשת.
 *
 * זה מפר את הכלל של אלי מ-24/09: „כל שגיאה אומרת מה לעשות ולאן ללכת."
 *
 * ⚠ 27/09/2026, אחרי זה: כל שאר ה-`fetch` הגולמיים תחת `src/api/` עברו
 *    לכאן או ל-`api()` בסריקה אחת. שלושה נשארו במקומם **במכוון** ורשומים
 *    שם: `uploadFiles`, `projectParts` ו-`pagePreview` ב-`api/uploads.ts`.
 */
export async function uploadForm<T>(
  path: string,
  body: FormData,
  method: ApiMethod = 'POST',
  opts: { fallbackMessage?: string } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { method, body, credentials: 'include' });
  } catch {
    throw new ApiError(
      0, 'NETWORK',
      'לא ניתן להגיע לשרת. יש לבדוק את חיבור הרשת ולנסות שוב.',
    );
  }

  if (res.status === 401) apiEvents.emit('unauthenticated');

  if (!res.ok) {
    const parsed = (await res.json().catch(() => ({}))) as Partial<AppErrorBody>;
    // 413 לא תמיד מגיע כ-JSON — nginx עונה HTML משלו כשהגוף גדול מדי, ואז
    // „שגיאת רשת" היה מסתיר בדיוק את הסיבה שהאדם יכול לתקן. אותו כלל בדיוק
    // כמו ב-`api()`, מאותה פונקציה.
    throw new ApiError(
      res.status,
      parsed.code ?? (res.status === 413 ? 'FILE_TOO_LARGE' : 'NETWORK'),
      parsed.message ?? statusMessage(res.status) ?? opts.fallbackMessage ?? 'שגיאת רשת',
      parsed.requestId,
      parsed.details,
    );
  }

  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(
      res.status, 'STALE_CLIENT',
      'הדף שפתוח מריץ גרסה ישנה של המערכת. יש לרענן את הדף עם Ctrl+F5 ולנסות שוב.',
    );
  }
}

/**
 * query string מאובייקט, בלי מפתחות ריקים.
 *
 * `undefined`, `''` ו-`false` נשמטים — ולכן קריאה בלי מסננים מפיקה נתיב נקי
 * ולא `?search=&onlyActive=false`. היה מוגדר פעמיים (api/catalog, api/catalogParts).
 */
export const qs = (o: Record<string, string | boolean | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '' && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};
